import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Menu, X, LogIn, LogOut, User as UserIcon, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import logoAsset from "@/assets/logo.png.asset.json";
const logo = logoAsset.url;
import { LanguageSwitcher } from "./LanguageSwitcher";
import { CurrencySwitcher } from "./CurrencySwitcher";
import { useAuth } from "@/hooks/useAuth";

export function Header() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { user, isAuthenticated, signOut } = useAuth();

  const links = [
    { to: "/for-businesses", label: t("nav.businesses") },
    { to: "/for-suppliers", label: t("nav.suppliers") },
    { to: "/for-partners", label: t("nav.partners") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/fleet", label: t("nav.fleet") },
    { to: "/about", label: t("nav.about") },
  ] as const;

  const initial = (user?.user_metadata?.full_name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-3" aria-label="Bacha Way Travel — home">
          <img src={logo} alt="" aria-hidden="true" width={40} height={40} className="h-10 w-10 rounded-full object-contain" />
          <span className="hidden sm:inline font-bold text-lg text-foreground tracking-tight">
            BACHA <span className="text-primary">WAY</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              activeProps={{ className: "text-primary" }}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <LanguageSwitcher />
          <CurrencySwitcher />
          {isAuthenticated ? (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                <Sparkles className="h-3 w-3" /> {t("auth.memberBadge")}
              </span>
              <Link
                to="/account"
                className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">{initial}</span>
                <span className="max-w-[120px] truncate">{user?.user_metadata?.full_name || user?.email}</span>
              </Link>
              <button
                onClick={() => signOut()}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground hover:text-primary"
                aria-label={t("auth.signOut")}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary"
            >
              <LogIn className="h-4 w-4" /> {t("nav.signIn")}
            </Link>
          )}
          <Link
            to="/booking"
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]"
          >
            {t("nav.book")}
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <CurrencySwitcher />
          <button
            className="text-foreground"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border lg:hidden">
          <nav className="flex flex-col gap-1 px-6 py-4">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-primary"
                activeProps={{ className: "bg-secondary text-primary" }}
              >
                {l.label}
              </Link>
            ))}
            {isAuthenticated ? (
              <>
                <Link
                  to="/account"
                  onClick={() => setOpen(false)}
                  className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground"
                >
                  <UserIcon className="h-4 w-4" /> {t("auth.myAccount")}
                </Link>
                <button
                  onClick={() => { setOpen(false); signOut(); }}
                  className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground"
                >
                  <LogOut className="h-4 w-4" /> {t("auth.signOut")}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground"
              >
                <LogIn className="h-4 w-4" /> {t("nav.signIn")} · {t("auth.memberBadge")}
              </Link>
            )}
            <Link
              to="/booking"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-full bg-primary px-5 py-2.5 text-center text-sm font-semibold text-primary-foreground"
            >
              {t("nav.book")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
